import { HTTPRequestResponse } from '@bimser/common';
import { BCScheduler, findMessage } from '@bimser/components';
import { createGuid, extend, FormItemTypes, IDictionary } from '@bimser/core';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import { IDataSource, IOnLoadArgs, IPropertyField } from '../../../../entities/common';
import { IDataSourceResponse } from '../../../../entities/common/DataSource/IDataSourceResponse';
import { IUserEvent } from '../../../../entities/services';
import { getDataSourceData } from '../../../../helper';
import CompositeDataBoundControl from '../../base/CompositeDataBoundControl';
import {
	IDataLoadEventArgs,
	IRowInsertingEventArgs,
	IRowRemovingEventArgs,
	IRowUpdatingEventArgs
} from '../../entities/EventArgs';
import { IDataGridColumnCollection, IDataGridRowCollection } from '../DataGrid/entities';
import Styles from './assets/style.scss';
import { IEditing, ISchedulerProperties, ViewTypes } from './entities';

dayjs.extend(localeData);

const viewTypes = {
	[ViewTypes.day]: { type: 'day' as const },
	[ViewTypes.week]: { type: 'week' as const },
	[ViewTypes.month]: { type: 'month' as const }
};

export default class Scheduler extends CompositeDataBoundControl {
	showTodayButton?: boolean; // Unsupported
	showViewSwitcher?: boolean; // Unsupported
	showAllDayPanel?: boolean;
	showDateNavigator?: boolean; // Unsupported
	showTooltip?: boolean; // Unsupported
	defaultView?: ViewTypes;
	startHour?: string;
	endHour?: string;
	supportedViews?: Array<ViewTypes>;
	editing?: IEditing;
	columns: IDataGridColumnCollection;
	rows: IDataGridRowCollection;
	dataSource: IDataSource;
	firstDayOfWeek: BCScheduler.Entities.SchedulerTypes.FirstDayOfWeek;

	constructor() {
		super();

		this.controlType = FormItemTypes.EControlTypes.Scheduler;

		this.columns = [];
		this.caption.position = 'top';
		this.showAllDayPanel = true;
		this.showTodayButton = true;
		this.showDateNavigator = true;
		this.showTooltip = true;
		this.showViewSwitcher = false;
		this.supportedViews = [ViewTypes.month, ViewTypes.week];
		this.defaultView = ViewTypes.week;
		this.rows = [];
		this.startHour = dayjs().set('hour', 8).set('minute', 30).format();
		this.endHour = dayjs().set('hour', 18).set('minute', 0).format();
		this.firstDayOfWeek = 1;
		this.editing = {
			enabled: false,
			addable: false,
			deletable: false,
			editable: false
		};

		this.onRowInserting = this.onRowInserting.bind(this);
		this.onRowUpdating = this.onRowUpdating.bind(this);
		this.onRowRemoving = this.onRowRemoving.bind(this);
	}

	__loadProperties(data: ISchedulerProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.showTodayButton = data.showTodayButton;
		this.__proxyChanged.showViewSwitcher = data.showViewSwitcher;
		this.__proxyChanged.showAllDayPanel = data.showAllDayPanel;
		this.__proxyChanged.showDateNavigator = data.showDateNavigator;
		this.__proxyChanged.showTooltip = data.showTooltip;
		this.__proxyChanged.defaultView = data.defaultView;
		this.__proxyChanged.startHour = data.startHour;
		this.__proxyChanged.endHour = data.endHour;
		this.__proxyChanged.editing = data.editing;
		this.__proxyChanged.supportedViews = data.supportedViews;
		this.__proxyChanged.columns = data.columns;
		this.__proxyChanged.rows = data.rows;
		this.__proxyChanged.dataSource = data.dataSource;
		this.__proxyChanged.firstDayOfWeek = data.firstDayOfWeek;
	}

	__getPropertyFields(): IPropertyField[] {
		return [
			...super.__getPropertyFields(),
			{
				name: 'showAllDayPanel',
				type: 'boolean'
			},
			{
				name: 'firstDayOfWeek',
				type: 'number'
			},
			{
				name: 'showTodayButton',
				type: 'boolean'
			},
			{
				name: 'showViewSwitcher',
				type: 'boolean'
			},
			{
				name: 'showDateNavigator',
				type: 'boolean'
			},
			{
				name: 'showTooltip',
				type: 'boolean'
			},
			{
				name: 'startHour',
				type: 'time'
			},
			{
				name: 'endHour',
				type: 'time'
			},
			{
				name: 'defaultView',
				type: 'enum',
				enumMembers: ['Day', 'Week', 'Month']
			},
			{
				name: 'editing',
				type: 'object',
				items: [
					{
						name: 'enabled',
						type: 'boolean'
					},
					{
						name: 'editable',
						type: 'boolean'
					},
					{
						name: 'addable',
						type: 'boolean'
					},
					{
						name: 'deleteable',
						type: 'boolean'
					}
				]
			}
		];
	}

	__onLoad({ externalData, reload, forceRefresh }: IOnLoadArgs) {
		if (!this.__isInitialized && this.dataSource?.parameters?.length && !reload) {
			return;
		}

		if (
			this.__form.serviceUrl &&
			this.__form.displayMode !== 'preview' &&
			this.dataSource?.name &&
			!this.dataSource.runAtServer
		) {
			this.__proxyChanging.loading = true;

			getDataSourceData({
				dataSource: this.dataSource,
				projectName: this.__form.projectName,
				clientCacheId: this.__form.__windowId,
				fieldParams: externalData?.fieldParams,
				reload,
				forceRefresh,
				projectVersion: this.__form.packageVersion
			})
				.then(response => this.__onDataSourceSuccess(response))
				.catch(() => {
					this.__proxyChanging.rows = [];
					this.__proxyChanging.loading = false;
				});
		}
	}

	private __onDataSourceSuccess(response: HTTPRequestResponse<IDataSourceResponse>) {
		const data = response?.data?.result;
		if (data && data.length) {
			const tId = this.__form.beginTransaction();

			this.__proxyChanging.rows = data.map((i, index) => {
				const data: any = {};
				const cells: Array<any> = [];
				this.columns.forEach((c, columnIndex) => {
					let value = i[c.sourceFieldKey || c.name];
					if (c?.dbType?.type === 'dateTime' && value) {
						value = new Date(value);
					} else if (c.name === 'id') {
						value = index;
					}

					data[c.name] = value;
					cells.push({
						editType: c.editType,
						index: columnIndex,
						entityPath: (c.editControl as any)?.EntityPath,
						name: c.name,
						text: c.caption[this.__form.currentLanguage.culture],
						value
					});
				});
				return { cells: cells, index, selected: false, data: data };
			});

			this.__form.endTransaction(tId).then(() => {
				this.__proxyChanging.loading = false;
				this.emit('event:OnDataLoad', { data: data } as IDataLoadEventArgs);
			});
		} else {
			this.__proxyChanging.loading = false;
		}
	}

	reload(forceRefresh?: boolean) {
		if (this.dataSource?.name) {
			if (this.dataSource?.parameters.length) {
				this.__reloadDataSource(this.dataSource, forceRefresh).catch(() => {
					this.__proxyChanging.loading = false;
				});
			} else {
				this.__onLoad({ reload: true, forceRefresh });
			}
		}
	}

	private onRowRemoving(e: BCScheduler.Entities.SchedulerTypes.AppointmentDeletingEvent) {
		e.cancel = new Promise<boolean>(async resolve => {
			const args: IRowRemovingEventArgs = {
				cancel: false,
				row: e.appointmentData,
				rows: this.rows
			};

			await this.emit('event:OnRowRemoving', args);

			if (args.cancel) {
				return resolve(true);
			}

			const [serverArgs] = await this.emit<IUserEvent<IRowRemovingEventArgs>>(
				'event:server:OnRowRemoving',
				{
					argument: args,
					controlId: this.name,
					name: 'OnRowRemoving',
					sender: 'web',
					guid: createGuid()
				}
			);

			if (serverArgs?.executedServerEvent?.parameters?.cancel) {
				return resolve(true);
			}

			resolve(false);
		});
	}

	private onRowInserting(e: BCScheduler.Entities.SchedulerTypes.AppointmentAddingEvent) {
		e.cancel = new Promise<boolean>(async resolve => {
			const args: IRowInsertingEventArgs = {
				cancel: false,
				row: e.appointmentData,
				rows: this.rows
			};

			await this.emit('event:OnRowInserting', args);

			if (args.cancel) {
				return resolve(true);
			}

			const [serverArgs] = await this.emit<IUserEvent<IRowInsertingEventArgs>>(
				'event:server:OnRowInserting',
				{
					argument: args,
					controlId: this.name,
					name: 'OnRowInserting',
					sender: 'web',
					guid: createGuid()
				}
			);

			if (serverArgs?.executedServerEvent?.parameters?.cancel) {
				return resolve(true);
			}

			resolve(false);
		});
	}

	private onRowUpdating(e: BCScheduler.Entities.SchedulerTypes.AppointmentUpdatingEvent) {
		e.cancel = new Promise<boolean>(async resolve => {
			const args: IRowUpdatingEventArgs = {
				cancel: false,
				newRow: e.newData,
				oldRow: e.oldData,
				rows: this.rows
			};

			await this.emit('event:OnRowUpdating', args);

			if (args.cancel) {
				return resolve(true);
			}

			const [serverArgs] = await this.emit<IUserEvent<IRowUpdatingEventArgs>>(
				'event:server:OnRowUpdating',
				{
					argument: args,
					controlId: this.name,
					name: 'OnRowUpdating',
					sender: 'web',
					guid: createGuid()
				}
			);

			if (serverArgs?.executedServerEvent?.parameters?.cancel) {
				return resolve(true);
			}

			resolve(false);
		});
	}

	getDataSource() {
		return this.rows.map(row => {
			const newRow: IDictionary<any> = {
				id: row.index
			};

			row.cells.forEach(cell => {
				const column = this.columns.find(col => col.name === cell.name);

				if (
					cell.value &&
					(column.editType === 'datePicker' || column.editType === 'dateTimePicker')
				) {
					const date = dayjs(cell.value);

					if (date.isValid()) {
						return (newRow[cell.name] = date.toDate());
					}
				}

				newRow[cell.name] = cell.value;
			});

			if (
				newRow.endDate &&
				newRow.startDate &&
				newRow.startDate.getTime() === newRow.endDate.getTime()
			) {
				newRow.endDate = new Date(newRow.startDate.getTime() + 1);
			}

			return newRow;
		});
	}

	getDayHours() {
		const startDayHour = dayjs(this.startHour).get('hour');
		const endDayHour = dayjs(this.endHour).get('hour');

		return [startDayHour, endDayHour];
	}

	__render() {
		const editable = !this.__isReadOnly() && !this.__isDisabled() && this.editing.enabled;
		const [startDayHour, endDayHour] = this.getDayHours();

		return super.__render(
			<BCScheduler.Components.Scheduler
				textExpr="title"
				firstDayOfWeek={this.firstDayOfWeek}
				dataSource={this.getDataSource()}
				startDayHour={startDayHour}
				endDayHour={endDayHour}
				showAllDayPanel={this.showAllDayPanel}
				onAppointmentAdding={this.onRowInserting}
				onAppointmentUpdating={this.onRowUpdating}
				onAppointmentDeleting={this.onRowRemoving}
				className={Styles.SchedulerBackgroundClass}
				defaultCurrentView={viewTypes[this.defaultView].type}
			>
				{!this.showViewSwitcher ? (
					<BCScheduler.Components.View type={viewTypes[this.defaultView].type} />
				) : (
					this.supportedViews.map(supportedView => (
						<BCScheduler.Components.View
							key={viewTypes[supportedView].type}
							type={viewTypes[supportedView].type}
						/>
					))
				)}
				<BCScheduler.Components.Editing
					allowAdding={editable && this.editing.addable}
					allowDeleting={editable && this.editing.deletable}
					allowUpdating={editable && this.editing.editable}
				/>
			</BCScheduler.Components.Scheduler>
		);
	}
}
